/**
 * Envío → «Límite diario»: el límite que el equipo lee en WhatsApp Manager (Límites de mensajes) y carga a mano.
 *
 * Twilio informa el límite de cada número, y a uno nuevo le dice «Unavailable» aunque el portfolio de Meta ya tenga
 * más (HATSU, 16/10/2026: 2.000 en WhatsApp Manager, 250 en Activa). Desde octubre de 2025 el límite es del
 * portfolio y lo comparten todos sus números. Lo cargado queda atado al número conectado y sólo vale mientras
 * Twilio no informe uno que se entienda: si informa, vale el de Twilio.
 *
 * Guarda sola, con su propio botón (`PATCH config { limiteMetaConfirmado }`): no es una clave de la config y no
 * pasa por el diff del formulario. Se muestra sólo si hace falta: Twilio no informa el límite, o ya hay algo cargado.
 */

import { useEffect, useId, useState } from "react";
import { CheckCircle2, Loader2, Save } from "lucide-react";
import type { CuentaPublica, LimiteMetaElegible } from "../../types";
import { oficialApi } from "../../api";
import { OPCIONES_LIMITE_META, limiteElegidoDe } from "../../logic/config";
import { fechaNegocio } from "../../logic/conversaciones";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";

const clave = (v: LimiteMetaElegible | null) => (v === null ? "" : String(v));
const deClave = (k: string): LimiteMetaElegible | null => (k === "" ? null : k === "ilimitado" ? "ilimitado" : (Number(k) as LimiteMetaElegible));

export function LimiteWhatsAppManager({ cuenta, recargar }: { cuenta: Pick<CuentaPublica, "limiteMeta">; recargar: () => Promise<void> | void }) {
  const id = useId();
  const l = cuenta.limiteMeta;
  const guardadoAhora = limiteElegidoDe(l?.confirmado);
  const [elegido, setElegido] = useState<LimiteMetaElegible | null>(guardadoAhora);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState("");
  // lo que vuelve del backend manda: al recargar, el selector muestra lo guardado
  useEffect(() => {
    setElegido(guardadoAhora);
  }, [guardadoAhora]);

  if (!l || (l.fuente === "twilio" && !l.confirmado)) return null;

  const guardar = async () => {
    if (guardando || elegido === guardadoAhora) return;
    setGuardando(true);
    setError("");
    setGuardado(false);
    try {
      const r = leerRespuesta(await oficialApi.actualizarConfig({ limiteMetaConfirmado: elegido }));
      if (!r.ok) { setError(textoError(r)); return; }
      await recargar();
      setGuardado(true);
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setGuardando(false);
    }
  };

  const estado =
    l.fuente === "equipo"
      ? `Twilio todavía no informa el límite de este número: se usa el que cargó el equipo${l.confirmado?.at ? ` el ${fechaNegocio(l.confirmado.at)}` : ""}.`
      : l.fuente === "twilio"
        ? "Twilio ya informa el límite de este número y vale ese. Si coincide con WhatsApp Manager, podés borrar lo cargado."
        : l.confirmado
          ? "Lo cargado se va a usar cuando Twilio lea el número (al verificar la cuenta). Hasta entonces, el mínimo: 250."
          : "Twilio todavía no informa el límite de este número y se usa el mínimo, 250. Si en WhatsApp Manager → Límites de mensajes figura otro, cargalo acá.";

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 space-y-2">
      <label htmlFor={id} className="text-sm font-medium text-slate-700">Límite que muestra WhatsApp Manager</label>
      <p className="text-xs text-slate-600">{estado}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <select id={id} value={clave(elegido)} onChange={(e) => { setElegido(deClave(e.target.value)); setGuardado(false); setError(""); }}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
          {OPCIONES_LIMITE_META.map((o) => <option key={clave(o.valor)} value={clave(o.valor)}>{o.etiqueta}</option>)}
        </select>
        <button type="button" onClick={() => void guardar()} disabled={guardando || elegido === guardadoAhora}
          className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
          {guardando ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar límite
        </button>
        {guardado && <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><CheckCircle2 size={15} /> Guardado</span>}
      </div>
      <p className="text-xs text-slate-400">Es del portfolio de Meta: lo comparten todos sus números, también los que no usa Activa. Si Twilio informa uno, vale el de Twilio.</p>
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
